import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CloudOutlined,
  WbSunny,
  Thunderstorm,
  AcUnit,
  Grain,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import { useEffect, useState } from "react";

interface WeatherData {
  temperature: number;
  condition: string;
  icon: string;
}

const WeatherWidget = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [currentTime, setCurrentTime] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [weather, setWeather] = useState<WeatherData>({
    temperature: 0,
    condition: "Loading...",
    icon: "cloud",
  });
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = useState(!isMobile);

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();

      const hours = now.getHours();
      const minutes = now.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = hours % 12 || 12;
      const formattedTime = `${hour12}:${minutes.toString().padStart(2, "0")} ${ampm}`;

      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const formattedDate = `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]}`;

      setCurrentTime(formattedTime);
      setCurrentDate(formattedDate);
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchWeatherByCoords = async (lat: number, lon: number) => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&temperature_unit=celsius`,
        );
        const data = await response.json();
        const weatherCode = data.current.weather_code;
        const { condition, icon } = getWeatherInfo(weatherCode);
        setWeather({
          temperature: Math.round(data.current.temperature_2m),
          condition,
          icon,
        });
        setLoading(false);
      } catch (error) {
        console.error("Weather fetch error:", error);
        setLoading(false);
      }
    };

    const fetchWeather = async () => {
      try {
        if (!navigator.geolocation) {
          fetchWeatherByCoords(-17.8252, 31.0335);
          return;
        }

        // Best-effort: if geolocation is already denied (or blocked by a
        // permissions policy), skip the call to avoid a console violation and
        // fall straight back to the Harare default.
        const permission = await navigator.permissions
          ?.query({ name: "geolocation" as PermissionName })
          .catch(() => undefined);
        if (permission?.state === "denied") {
          fetchWeatherByCoords(-17.8252, 31.0335);
          return;
        }

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            const response = await fetch(
              `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=celsius`,
            );
            const data = await response.json();
            const weatherCode = data.current.weather_code;
            const { condition, icon } = getWeatherInfo(weatherCode);
            setWeather({
              temperature: Math.round(data.current.temperature_2m),
              condition,
              icon,
            });
            setLoading(false);
          },
          () => {
            // Permission denied / unavailable is expected and recoverable —
            // fall back silently to the default location.
            fetchWeatherByCoords(-17.8252, 31.0335);
          },
        );
      } catch (error) {
        console.error("Weather fetch error:", error);
        setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getWeatherInfo = (code: number) => {
    if (code === 0) return { condition: "Clear", icon: "sunny" };
    if (code <= 3) return { condition: "Partly Cloudy", icon: "cloud" };
    if (code <= 48) return { condition: "Foggy", icon: "cloud" };
    if (code <= 67) return { condition: "Rainy", icon: "rain" };
    if (code <= 77) return { condition: "Snowy", icon: "snow" };
    if (code <= 82) return { condition: "Showers", icon: "rain" };
    if (code <= 99) return { condition: "Thunderstorm", icon: "thunder" };
    return { condition: "Unknown", icon: "sunny" };
  };

  const getWeatherIcon = (size = 20) => {
    const iconProps = { fontSize: size, color: colors.primary[100] };
    switch (weather.icon) {
      case "sunny":
        return <WbSunny sx={iconProps} />;
      case "rain":
        return <Grain sx={iconProps} />;
      case "snow":
        return <AcUnit sx={iconProps} />;
      case "thunder":
        return <Thunderstorm sx={iconProps} />;
      default:
        return <CloudOutlined sx={iconProps} />;
    }
  };

  if (!expanded) {
    return (
      <Box
        onClick={() => setExpanded(true)}
        sx={{
          position: "fixed",
          bottom: { xs: 20, md: 50 },
          left: { xs: 20, md: 50 },
          zIndex: 100,
          width: 52,
          height: 52,
          borderRadius: "50%",
          backgroundColor: colors.primary[900],
          border: `1px solid ${colors.primary[300]}30`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          gap: 0.25,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          transition: "transform 0.2s ease",
          "&:hover": { transform: "scale(1.05)" },
        }}
      >
        {getWeatherIcon(20)}
        <Typography
          sx={{
            color: colors.primary[100],
            fontSize: "0.6rem",
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {loading ? "..." : `${weather.temperature}°`}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={() => setExpanded(false)}
      sx={{
        position: "fixed",
        bottom: { xs: 12, md: 50 },
        left: { xs: 12, md: 50 },
        zIndex: 100,
        cursor: "pointer",
        transition: "opacity 0.2s ease",
        "&:hover": { opacity: 0.85 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          borderRadius: 50,
          backgroundColor: colors.primary[900],
          border: `1px solid ${colors.primary[300]}30`,
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}
      >
        <Box sx={{ px: 3, py: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.primary[100],
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            {currentTime}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: colors.primary[700], fontSize: "0.75rem" }}
          >
            {currentDate}
          </Typography>
        </Box>

        <Box
          sx={{
            width: "1px",
            height: 40,
            backgroundColor: colors.primary[300] + "40",
          }}
        />

        <Box sx={{ px: 3, py: 1.5 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
          >
            {getWeatherIcon(20)}
            <Typography
              variant="body2"
              sx={{
                color: colors.primary[100],
                fontWeight: 600,
                fontSize: "0.9rem",
              }}
            >
              {loading ? "..." : `${weather.temperature}°C`}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: colors.primary[700], fontSize: "0.75rem" }}
          >
            {weather.condition}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default WeatherWidget;
